import { computed, type ComputedRef, ref, type Ref } from 'vue'
import { defineStore } from 'pinia'
import {
  AssignmentScopeEnum,
  AssignOnTransferEnum,
  type AuthoritiesEnum,
  DistributionModeEnum,
  TransferScopeEnum,
  type UserSessionDTOV1,
} from '@/common/types/session'
import { BulkActionEnum } from '@/common/types/bulkAction'
import * as localeHelpers from '@/common/helpers/locale'
import { type UnprotectedParameterEnum, type InstanceParameterDTOV1 } from '@/common/types/instanceParameter'
import { getUserMe } from '@/common/services/UsersService'
import { getParameterList, updateParameterValue } from '@/common/services/InstanceParameterService'
import { NULLUUID } from '@/common/types/general'
import { roleAuthorityPrefix, type RoleType } from '@/common/types/roles'

export const useSessionStore = defineStore('session', () => {
  const id: Ref<number> = ref(0)
  const fullName: Ref<string> = ref('')
  const engageChannelsAuthority: Ref<boolean> = ref(false)
  const authorities: Ref<AuthoritiesEnum[]> = ref([])
  const spaces: Ref<string[]> = ref([])
  const skills: Ref<string[]> = ref([])
  const distributionMode: Ref<DistributionModeEnum> = ref(DistributionModeEnum.PULL_EXPLICIT_ASSIGN)
  const pushTempo: Ref<number> = ref(0)
  const displayAvailableTicketCount: Ref<boolean> = ref(false)
  const timeZone: Ref<string> = ref(Intl.DateTimeFormat().resolvedOptions().timeZone) // navigator timezone by default
  const locale: Ref<string> = ref(localeHelpers.getBrowserLocal())
  const defaultCountryPhoneCode: Ref<number> = ref(0)
  const state: Ref<string | null> = ref(null)
  const screenIdTicketList: Ref<number> = ref(0)
  const screenIdClientList: Ref<number> = ref(0)
  const screenIdInteractionList: Ref<number> = ref(0)
  const screenIdCompanyList: Ref<number> = ref(0)
  const screenIdClientEdit: Ref<number> = ref(0)
  const searchConfigs: Ref<UUID[]> = ref([])
  const instanceParameters: Ref<InstanceParameterDTOV1[]> = ref([])
  const transferScope: Ref<TransferScopeEnum> = ref(TransferScopeEnum.SKILLED_ONLY)
  const assignmentScope = ref<AssignmentScopeEnum>(AssignmentScopeEnum.SKILL_SUBSCRIBERS)
  const trainedSkills: Ref<string[]> = ref([])
  const assignOnTransfer: Ref<AssignOnTransferEnum> = ref(AssignOnTransferEnum.NOT_ALLOWED)
  const login: Ref<string> = ref('')
  const oidcEnabled: Ref<boolean> = ref(false)
  const teamCode: Ref<UUID> = ref(NULLUUID)
  const teams: Ref<UUID[]> = ref([])
  const assignmentRestrictedToTeamsMembers = ref(true)

  async function loadSessionSettings() {
    const [session, parameters] = await Promise.all([getUserMe(), getParameterList()])

    if (session) {
      populateSessionValues(session)
      handleOptionalSessionProperties(session)
    }

    if (parameters.length) {
      instanceParameters.value = [...parameters]
    }
  }

  function populateSessionValues(session: UserSessionDTOV1) {
    id.value = session.id
    fullName.value = session.fullName
    authorities.value = [...session.authorities]
    spaces.value = [...session.spaces]
    skills.value = [...session.skills]
    distributionMode.value = session.distributionMode
    state.value = session.state
    timeZone.value = session.timeZone
    login.value = session.login
    teamCode.value = session.teamCode
    teams.value = session.teams
    screenIdTicketList.value = session.screenIdTicketList
    screenIdClientList.value = session.screenIdClientList
    screenIdInteractionList.value = session.screenIdInteractionList
    screenIdCompanyList.value = session.screenIdCompanyList
    screenIdClientEdit.value = session.screenIdClientEdit
    searchConfigs.value = [...session.searchConfigs]
    assignmentScope.value = session.assignmentScope
    assignmentRestrictedToTeamsMembers.value = session.assignmentRestrictedToTeamsMembers
  }

  function handleOptionalSessionProperties(session: UserSessionDTOV1) {
    if (session.transferScope) transferScope.value = session.transferScope
    if (session.assignOnTransfer) assignOnTransfer.value = session.assignOnTransfer
    if (session.displayAvailableTicketCount) displayAvailableTicketCount.value = true
    if (session.trainedSkills) trainedSkills.value = [...session.trainedSkills]
    if (session.locale) locale.value = session.locale
    if (session.defaultCountryPhoneCode) defaultCountryPhoneCode.value = session.defaultCountryPhoneCode

    if (distributionMode.value === DistributionModeEnum.PUSH && session.pushTempo) {
      pushTempo.value = session.pushTempo
    }
  }

  const isPushAuto = computed(() => {
    return distributionMode.value === DistributionModeEnum.PUSH
  })

  const localeLanguage: ComputedRef<string> = computed(() => {
    return locale.value.slice(0, 2)
  })

  const getAuthoritySet = computed(() => {
    return new Set<string>(authorities.value)
  })

  const getUserRolesSet = computed(() => {
    return new Set(
      authorities.value.reduce<RoleType[]>((acc, auth) => {
        if (auth.startsWith(roleAuthorityPrefix)) {
          acc.push(auth.slice(roleAuthorityPrefix.length) as RoleType)
        }
        return acc
      }, []),
    )
  })

  const getParameterMap = computed(() => {
    return new Map<UnprotectedParameterEnum, InstanceParameterDTOV1>(instanceParameters.value.map((p) => [p.code, p]))
  })

  const getInstanceParameterValue = computed(() => {
    return (parameterKey: UnprotectedParameterEnum) => {
      return getParameterMap.value.get(parameterKey)?.value
    }
  })

  const getAvailableCasesBulkActions: ComputedRef<string[]> = computed(() => {
    return Object.keys(BulkActionEnum).filter((action) => getAuthoritySet.value.has(action))
  })

  const updateInstanceParameter = async (code: UnprotectedParameterEnum, value: InstanceParameterDTOV1['value']) => {
    await updateParameterValue(code, value)

    const parameterIndex = instanceParameters.value.findIndex((item) => item.code === code)
    if (parameterIndex === -1) return

    const parameter = getParameterMap.value.get(code)
    if (!parameter) return

    instanceParameters.value.splice(parameterIndex, 1, {
      ...parameter,
      value,
    })

    return getParameterMap.value.get(code)
  }

  return {
    id,
    fullName,
    engageChannelsAuthority,
    authorities,
    spaces,
    skills,
    defaultCountryPhoneCode,
    trainedSkills,
    distributionMode,
    pushTempo,
    displayAvailableTicketCount,
    timeZone,
    locale,
    state,
    screenIdTicketList,
    screenIdClientList,
    screenIdInteractionList,
    screenIdCompanyList,
    screenIdClientEdit,
    searchConfigs,
    instanceParameters,
    localeLanguage,
    transferScope,
    assignmentScope,
    assignOnTransfer,
    login,
    getAuthoritySet,
    getAvailableCasesBulkActions,
    getUserRolesSet,
    getParameterMap,
    getInstanceParameterValue,
    oidcEnabled,
    isPushAuto,
    teamCode,
    teams,
    assignmentRestrictedToTeamsMembers,
    updateInstanceParameter,
    loadSessionSettings,
  }
})
